import { useState } from "react";

import type { BacktestListFilters } from "../bots/backtest-queries.js";
import { ProblemNotice } from "../bots/problem-notice.js";
import { Button } from "../../shared/ui/button.js";
import {
  useDeleteFilteredBacktests,
  usePreviewFilteredDeletion,
} from "./filtered-deletion-queries.js";

export function DeleteFilteredBacktests({
  filters,
  onBusyChange,
}: {
  readonly filters: BacktestListFilters;
  readonly onBusyChange: (busy: boolean) => void;
}) {
  const preview = usePreviewFilteredDeletion(() => onBusyChange(false));
  const [confirmed, setConfirmed] = useState(false);
  const [processed, setProcessed] = useState(0);
  const deletion = useDeleteFilteredBacktests(setProcessed, () => onBusyChange(false));
  const currentPreview =
    preview.data?.filtersKey === JSON.stringify(filters) ? preview.data : undefined;
  const plan = currentPreview?.plan;
  const busy = preview.isPending || deletion.isPending;

  return (
    <div className="content-card section-stack" aria-label="Delete filtered backtest results">
      <div className="button-row">
        <Button
          disabled={busy}
          onClick={() => {
            setConfirmed(false);
            preview.reset();
            deletion.reset();
            onBusyChange(true);
            preview.mutate(filters);
          }}
          tone="danger"
        >
          {preview.isPending ? "Checking all matching results…" : "Delete filtered results"}
        </Button>
        <span>Includes all pages. Queued and running backtests are not deleted.</span>
      </div>

      {plan === undefined || deletion.data !== undefined ? null : (
        <div
          className="delete-confirmation"
          role="alertdialog"
          aria-label="Confirm filtered deletion"
        >
          <div className="section-stack">
            <strong>Delete {plan.targets.length} filtered results permanently?</strong>
            <p>
              Matched {plan.totalCount} runs across all pages; skipped {plan.activeCount} active
              runs. Only these previewed results will be deleted. New runs are not included.
            </p>
            <p>
              Scope:{" "}
              {Object.keys(filters).length === 0
                ? "All backtest results (no filters)."
                : Object.entries(filters)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join(" · ")}
            </p>
            <p>
              The run records and stored evidence cannot be recovered. Bots and live trading remain
              unchanged.
            </p>
            {plan.targets.length === 0 ? (
              <p>No deletable results match these filters.</p>
            ) : (
              <label>
                <input
                  type="checkbox"
                  checked={confirmed}
                  disabled={busy}
                  onChange={(event) => setConfirmed(event.currentTarget.checked)}
                />{" "}
                I understand these {plan.targets.length} results will be permanently deleted.
              </label>
            )}
            <div className="button-row">
              <Button
                disabled={busy}
                onClick={() => {
                  preview.reset();
                  setConfirmed(false);
                }}
                tone="quiet"
              >
                Keep results
              </Button>
              <Button
                disabled={busy || !confirmed || plan.targets.length === 0}
                onClick={() => {
                  setProcessed(0);
                  onBusyChange(true);
                  deletion.mutate(plan);
                }}
                tone="danger"
              >
                {deletion.isPending
                  ? "Deleting…"
                  : `Delete ${plan.targets.length} results permanently`}
              </Button>
            </div>
            {deletion.isPending ? (
              <p role="status">
                Processed {processed} of {plan.targets.length} results.
              </p>
            ) : null}
          </div>
        </div>
      )}
      {preview.error === null ? null : <ProblemNotice error={preview.error} />}
      {deletion.error === null ? null : <ProblemNotice error={deletion.error} />}
      {deletion.data === undefined ? null : (
        <div role="status" className="section-stack">
          <strong>
            Deleted {deletion.data.deleted.length} of {deletion.variables?.targets.length} results.
            Failed: {deletion.data.failed.length}.
          </strong>
          <p>
            Skipped {deletion.variables?.activeCount} active runs. Refresh and preview again before
            retrying any failures.
          </p>
          {deletion.data.failed.length === 0 ? null : (
            <ul>
              {deletion.data.failed.map(({ target, reason }) => (
                <li key={target.id}>
                  {target.label} ({target.id}): {reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
